import type { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios'
import { getReporter } from './reporter'

/** metrics endpoint 路径片段，用于过滤自身请求 */
const METRICS_PATH = '/common/metrics/web-vitals'

/** 扩展 axios config 携带计时起点和 trace_id */
interface TimedConfig extends InternalAxiosRequestConfig {
  __metricsStart?: number
  __traceId?: string
}

/**
 * 为 axios 实例注册 API 性能监控拦截器
 *
 * 必须在业务拦截器（setupInterceptors）之前调用，
 * 确保响应拦截器先于业务拦截器执行，能拿到原始 AxiosResponse。
 * 请求拦截器因 LIFO 顺序会在业务拦截器之后执行，
 * 因此读取业务拦截器已设置的 X-Trace-Id 而非自行生成。
 */
export function setupApiMetrics(axiosInstance: AxiosInstance): void {
  axiosInstance.interceptors.request.use((config: TimedConfig) => {
    const url = config.url || ''
    if (!url.includes(METRICS_PATH)) {
      config.__metricsStart = performance.now()
      const existingTraceId = config.headers?.get('X-Trace-Id')
      if (typeof existingTraceId === 'string') {
        config.__traceId = existingTraceId
      } else {
        delete config.__traceId
      }
    }
    return config
  })

  axiosInstance.interceptors.response.use(
    response => {
      const config = response?.config as TimedConfig | undefined
      if (config) recordMetric(config, response.status, false)
      return response
    },
    error => {
      const axiosError = error as AxiosError | undefined
      const config = (axiosError?.config || {}) as TimedConfig
      const status = axiosError?.response?.status || 0
      const isTimeout = axiosError?.code === 'ECONNABORTED'
      const isAbort = axiosError?.code === 'ERR_CANCELED'
      recordMetric(config, status, true, isTimeout, isAbort)
      return Promise.reject(
        error instanceof Error ? error : new Error('Metrics interceptor failed')
      )
    }
  )
}

/** 记录单次 API 请求指标 */
function recordMetric(
  config: TimedConfig,
  status: number,
  error: boolean,
  timeout = false,
  abort = false
): void {
  if (!config.__metricsStart) return

  const reporter = getReporter()
  if (!reporter) return

  const duration = performance.now() - config.__metricsStart
  const url = config.url || ''

  reporter.addApiMetric({
    url,
    method: (config.method || 'get').toUpperCase(),
    duration,
    status,
    error,
    ...(config.__traceId === undefined ? {} : { traceId: config.__traceId }),
    timeout,
    abort,
  })
}
